// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router';

import AnnotationPageComponent from 'components/annotation-page/annotation-page';
import {
    getJobAsync, saveLogsAsync, changeFrameAsync,
    closeJob as closeJobAction,
} from 'actions/annotation-actions';
import { getJobAsync as getReferenceJobAsync } from 'actions/reference-annotation-actions';
import { hydrateColorSettings } from 'actions/settings-actions';

import { CombinedState, Workspace } from 'reducers';
import { routeJob, staleJob } from 'utils/workspace-route';

// drawings and references share this container, so switching between them keeps the page mounted
type OwnProps = RouteComponentProps<{
    tid: string;
    jid: string;
    rid?: string;
}>;

interface StateToProps {
    job: any | null | undefined;
    staleJob: any | null;
    frameNumber: number;
    fetching: boolean;
    workspace: Workspace;
    saving: boolean;
}

interface DispatchToProps {
    getJob(): void;
    changeFrame(frame: number): void;
    saveLogs(): void;
    closeJob(): void;
    hydrateColorSettings(jobId?: number | null, frame?: number | null): void;
}

function mapStateToProps(state: CombinedState, own: OwnProps): StateToProps {
    const { params } = own.match;
    const jobID = +params.jid;
    const {
        annotation: {
            job: { requestedId, instance: job, fetching },
            workspace,
            player: {
                frame: {
                    number: frameNumber,
                },
            },
            annotations: {
                saving: { uploading: saving },
            },
        },
    } = state;

    const routed = routeJob(job, requestedId, jobID, params.rid === undefined ? 'task' : 'reference');
    return {
        job: routed,
        staleJob: staleJob(job, routed),
        fetching,
        workspace,
        frameNumber,
        saving,
    };
}

function mapDispatchToProps(dispatch: any, own: OwnProps): DispatchToProps {
    const { params } = own.match;
    const taskID = +params.tid;
    const jobID = +params.jid;
    const searchParams = new URLSearchParams(window.location.search);
    const initialFilters: object[] = [];
    const initialOpenGuide = searchParams.has('openGuide');
    const parsedFrame = +(searchParams.get('frame') || 'NaN');
    const initialFrame = Number.isInteger(parsedFrame) && parsedFrame >= 0 ? parsedFrame : null;

    if (searchParams.has('serverID') && searchParams.has('type')) {
        const serverID = searchParams.get('serverID');
        const type = searchParams.get('type');
        if (serverID && !Number.isNaN(+serverID)) {
            initialFilters.push({
                and: [{ '==': [{ var: 'serverID' }, serverID] }, { '==': [{ var: 'type' }, type] }],
            });
        }
    }

    return {
        getJob(): void {
            // not during render: the page stays mounted across routes, so this runs on an update
            if (searchParams.size) {
                own.history.replace(own.history.location.pathname);
            }
            if (params.rid !== undefined) {
                dispatch(getReferenceJobAsync({
                    referenceID: +params.rid, jobID, initialFrame, initialFilters, initialOpenGuide,
                }));
                return;
            }
            dispatch(getJobAsync({
                taskID,
                jobID,
                initialFrame,
                initialFilters,
                initialOpenGuide,
            }));
        },
        saveLogs(): void {
            dispatch(saveLogsAsync());
        },
        closeJob(): void {
            dispatch(closeJobAction());
        },
        changeFrame(frame: number): void {
            dispatch(changeFrameAsync(frame));
        },
        hydrateColorSettings(jobId?: number | null, frame?: number | null): void {
            dispatch(hydrateColorSettings(jobId, frame));
        },
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AnnotationPageComponent));
